import { ReactNode } from 'react';

import './FrostedGlass.css';

type FrostedGlassProps = {
  /** Content to render inside the frosted glass container */
  children?: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Show the inner glow gradient overlay (default: true) */
  withGlow?: boolean;
  /**
   * Wrap children in a relative z-10 div to ensure they appear above effect layers.
   * Set to false if you need direct children (e.g., for flex layouts) and will
   * handle z-index yourself. (default: true)
   */
  wrapChildren?: boolean;
  /** Additional props passed to the container div */
  [key: string]: unknown;
};

/**
 * A frosted glass effect container component.
 *
 * Applies a translucent, blurred background effect similar to Apple's "vibrancy" style.
 * The effect includes:
 * - Semi-transparent white background
 * - Strong backdrop blur
 * - Increased saturation for richer colors
 * - Inner glow gradient (optional, enabled by default)
 * - Border highlight and noise texture (via CSS)
 *
 * @example
 * ```tsx
 * <FrostedGlass className="rounded-2xl p-4">
 *   <p>Content with frosted glass background</p>
 * </FrostedGlass>
 *
 * // Without children wrapper (for flex layouts)
 * <FrostedGlass wrapChildren={false} className="flex flex-col">
 *   <div className="relative z-10">Item 1</div>
 *   <div className="relative z-10">Item 2</div>
 * </FrostedGlass>
 * ```
 */
export default function FrostedGlass({
  children,
  className,
  withGlow = true,
  wrapChildren = true,
  ...restProps
}: FrostedGlassProps) {
  const baseClasses =
    'frosted-glass relative bg-white/50 dark:bg-gray-90/50 backdrop-blur-3xl backdrop-saturate-[1.8]';
  const combinedClassName = className
    ? `${baseClasses} ${className}`
    : baseClasses;

  return (
    <div className={combinedClassName} {...restProps}>
      {withGlow && (
        <div
          className="dark:from-gray-90/40 dark:to-gray-90/20 bg-linear-to-b pointer-events-none absolute inset-0 rounded-[inherit] from-white/30 via-transparent to-white/10"
          aria-hidden="true"
        />
      )}
      {wrapChildren ? (
        <div className="relative z-10">{children}</div>
      ) : (
        children
      )}
    </div>
  );
}
